import {
  Accordion,
  ActionIcon,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  Flex,
  Group,
  Radio,
  Select,
  Text,
  Textarea,
  rem,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import moment from "moment";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  createMar,
  getMar,
  updateMedicationAdministration,
} from "../../services";
import {
  decrypt,
  firstLetterCap,
  showInfo,
  getActiveSlotForDate,
} from "../../utils";
import useStyles from "./style";
import DeleteComponent from "./DeleteComponent";
import { IconClock } from "@tabler/icons-react";
import { DateInput, DatePickerInput, TimeInput } from "@mantine/dates";
import { useRef } from "react";
import swal from "sweetalert";
import { IconChevronDown } from "@tabler/icons-react";
import dayjs from "dayjs";
import { getColor } from "../../pages/Home/Medication";
import takenMed from "../../images/takenMed.png";
import refusedMed from "../../images/refusedMed.png";
import unableToTakeMed from "../../images/unableToTakeMed.png";
import outOfMed from "../../images/outOfMed.png";
import { TiMinus } from "react-icons/ti";
import { FaPlus } from "react-icons/fa6";
import { FiCalendar } from "react-icons/fi";

const MedicationModal = ({
  date,
  opened,
  onclose,
  data,
  allData,
  isViewOpen,
  futureDate,
  isActivity,
}) => {
  console.log("🚀 ~ MedicationModal ~ data:", data);
  console.log("🚀 ~ MedicationModal ~ allData:", allData);
  const ref = useRef(null);
  const { profile } = useSelector((state) => state.profile);
  const getCalendarDateKey = (value) => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return moment(value, "MM/DD/YYYY").format("YYYY-MM-DD");
    }
    // Use local (browser) timezone format so the returned date matches the
    // calendar cell the user actually sees.  For UTC+ users (e.g. PKT UTC+5)
    // a Date at midnight local is T19:00:00Z the previous day — calling .utc()
    // would give the wrong (prior) day.  Virtual entries use noon UTC so they
    // are safe under both formats; DB records with a midnight-local timestamp
    // are the edge case that requires local formatting.
    return moment(value).format("YYYY-MM-DD");
  };

  const buildCalendarIso = (dateValue, timeValue) => {
    const dateKey = getCalendarDateKey(dateValue);
    const parsedTime = timeValue
      ? moment(timeValue, ["HH:mm", "hh:mm A"], true)
      : null;
    const timePart =
      parsedTime && parsedTime.isValid()
        ? parsedTime.format("HH:mm:ss.SSS")
        : "00:00:00.000";
    return moment(`${dateKey}T${timePart}`).utc().toISOString();
  };

  const _medicationType = (() => {
    const mt = data?.medicationType;
    if (Array.isArray(mt)) {
      const entrySlot = getActiveSlotForDate(data?.timeSlot, data?.date);

      // isPast: true only when the scheduled time has actually passed.
      // For today's medicines we check the slot time — not just the calendar day —
      // so a medicine at 3 PM is still "upcoming" when viewed at 2 PM.
      const _checkDate = moment(date || data?.date);
      const _today = moment().startOf("day");
      const isPast = (() => {
        if (_checkDate.isBefore(_today, "day")) return true;
        if (_checkDate.isAfter(_today, "day")) return false;
        // Today: check whether the slot time has already passed
        if (entrySlot) {
          const _slotMoment = moment(entrySlot, ["hh:mm A", "HH:mm"]);
          if (_slotMoment.isValid()) {
            const nowInBiz = moment();
            return nowInBiz.isSameOrAfter(
              _today.clone().set({
                hour: _slotMoment.hours(),
                minute: _slotMoment.minutes(),
                second: 0,
                millisecond: 0,
              }),
            );
          }
        }
        return false;
      })();

      for (const targetDate of [date, data?.date]) {
        if (!targetDate) continue;
        const targetDateStr = getCalendarDateKey(targetDate);

        const exactMatches = mt.filter(
          (m) =>
            getCalendarDateKey(m.date) === targetDateStr &&
            m.timeSlot === entrySlot,
        );
        if (exactMatches.length > 0) {
          const status = exactMatches[exactMatches.length - 1].status;
          if (typeof status === "string" && status.toLowerCase() === "upcoming" && isPast) return "Missed";
          return status;
        }

        const dateOnlyMatches = mt.filter(
          (m) => getCalendarDateKey(m.date) === targetDateStr && !m.timeSlot,
        );
        if (dateOnlyMatches.length > 0) {
          const status = dateOnlyMatches[dateOnlyMatches.length - 1].status;
          if (typeof status === "string" && status.toLowerCase() === "upcoming" && isPast) return "Missed";
          return status;
        }
      }

      // No match for this date+slot — derive virtual status from date.
      return isPast ? "Missed" : "upcoming";
    }
    return mt ?? "upcoming";
  })();
  const _dosageFromEntry = (() => {
    const mt = data?.medicationType;
    if (!Array.isArray(mt)) return data?.dosage ?? 0;
    const entrySlot = getActiveSlotForDate(data?.timeSlot, data?.date);
    for (const targetDate of [date, data?.date]) {
      if (!targetDate) continue;
      const targetDateStr = getCalendarDateKey(targetDate);
      const match =
        mt
          .filter(
            (m) =>
              getCalendarDateKey(m.date) === targetDateStr &&
              m.timeSlot === entrySlot,
          )
          .pop() ||
        mt.filter((m) => getCalendarDateKey(m.date) === targetDateStr).pop();
      if (match) return match.dosage ?? data?.dosage ?? 0;
    }
    return data?.dosage ?? 0;
  })();
  const _outOfMedication = "Out of Medication";
  const colors = getColor(_medicationType);
  const { classes } = useStyles({
    darkColor: colors?.dark,
    lightColor: colors?.light,
    isOutOfMedication: _medicationType === _outOfMedication,
  });
  const isMobileScreen = useMediaQuery("(max-width: 600px)");
  const _upcoming = "upcoming";
  const { staff } = useSelector((state) => state?.staff);

  const [validation, setValidation] = useState({
    outcome: (value) => (value ? null : "Required"),
    comments: (value) => (value ? null : "Required"),
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { selectedClient } = useSelector((state) => state.mar);
  let userid = profile?.user?.id;
  let userType = profile?.user?.type;
  let role = profile?.user?.role;
  const [selectedStaff, setSelectedStaff] = useState();
  const { allSignature } = useSelector((state) => state?.signature);

  const assignedStaffIds = selectedClient?.staff
    ?.filter((s) => s?.isAssign === true)
    ?.map((s) => s?.staffId);

  const populateStaff = staff?.staffs
    ?.filter((staff) => assignedStaffIds?.includes(staff?.id))
    ?.map((staff) => ({
      label: staff?.name,
      value: staff?.id,
    }));

  const form = useForm({
    initialValues: {
      quantity: "0",
      reason: "",
      comments: "",
      outcome: "",
      createdAt: null, // added for the date/time picker
    },
    validate: validation,
  });

  useEffect(() => {
    setValidation((prevValidation) => ({
      ...prevValidation,
    }));
  }, [form?.values?.outcome]);

  // ── FIX: Resolve the staff object for the current status entry. ───────────
  // For statuses stored inside the medicationType array (Refused, Out of
  // Medication, Unable to Take, Taken), the staff is embedded in the matching
  // array entry — NOT at the top-level data.staff.  Reading only data.staff
  // caused "Administered by" and "Signature by" to be blank for all statuses
  // except legacy Taken records that stored staff at the top level.
  const resolvedStaff = (() => {
    const mt = data?.medicationType;
    if (Array.isArray(mt) && mt.length > 0) {
      const entrySlot = getActiveSlotForDate(data?.timeSlot, data?.date);

      for (const targetDate of [date, data?.date]) {
        if (!targetDate) continue;
        const targetDateStr = getCalendarDateKey(targetDate);

        const exactMatches = mt.filter(
          (m) =>
            getCalendarDateKey(m.date) === targetDateStr &&
            m.timeSlot === entrySlot,
        );
        if (
          exactMatches.length > 0 &&
          exactMatches[exactMatches.length - 1].staff
        )
          return exactMatches[exactMatches.length - 1].staff;

        const dateOnlyMatches = mt.filter(
          (m) => getCalendarDateKey(m.date) === targetDateStr && !m.timeSlot,
        );
        if (
          dateOnlyMatches.length > 0 &&
          dateOnlyMatches[dateOnlyMatches.length - 1].staff
        )
          return dateOnlyMatches[dateOnlyMatches.length - 1].staff;

        const anyDateMatches = mt.filter(
          (m) => getCalendarDateKey(m.date) === targetDateStr,
        );
        if (
          anyDateMatches.length > 0 &&
          anyDateMatches[anyDateMatches.length - 1].staff
        )
          return anyDateMatches[anyDateMatches.length - 1].staff;
      }

      if (mt[mt.length - 1]?.staff) return mt[mt.length - 1].staff;
    }
    return data?.staff;
  })();
  // ─────────────────────────────────────────────────────────────────────────

  // Resolve the administeredAt from the matching medicationType entry.
  // New records store administeredAt directly; old records fall back to the
  // entry's date field, then to the top-level createdAt.
  const resolvedAdministeredAt = (() => {
    const mt = data?.medicationType;
    if (Array.isArray(mt) && mt.length > 0) {
      const entrySlot = getActiveSlotForDate(data?.timeSlot, data?.date);

      for (const targetDate of [date, data?.date]) {
        if (!targetDate) continue;
        const targetDateStr = getCalendarDateKey(targetDate);

        const exactMatches = mt.filter(
          (m) =>
            getCalendarDateKey(m.date) === targetDateStr &&
            m.timeSlot === entrySlot,
        );
        if (exactMatches.length > 0) {
          const entry = exactMatches[exactMatches.length - 1];
          return entry.administeredAt || entry.date || data?.createdAt;
        }

        const dateOnlyMatches = mt.filter(
          (m) => getCalendarDateKey(m.date) === targetDateStr && !m.timeSlot,
        );
        if (dateOnlyMatches.length > 0) {
          const entry = dateOnlyMatches[dateOnlyMatches.length - 1];
          return entry.administeredAt || entry.date || data?.createdAt;
        }

        const anyDateMatches = mt.filter(
          (m) => getCalendarDateKey(m.date) === targetDateStr,
        );
        if (anyDateMatches.length > 0) {
          const entry = anyDateMatches[anyDateMatches.length - 1];
          return entry.administeredAt || entry.date || data?.createdAt;
        }
      }
    }
    return data?.createdAt;
  })();

  // medicineRemaining comes from the API at the top level of data (not inside the
  // medicationType array entries). Show "-" if the key is missing or null.
  const resolvedMedicineRemaining =
    data?.medicineRemaining != null && data?.medicineRemaining !== "null"
      ? data.medicineRemaining
      : undefined;

  const adminForm = useForm({
    initialValues: {
      staff:
        resolvedStaff?.id ||
        resolvedStaff?._id?.toString() ||
        data?.staff?.id ||
        data?.staff?._id?.toString(),
      date: resolvedAdministeredAt ? new Date(resolvedAdministeredAt) : null,
      time: resolvedAdministeredAt
        ? moment(resolvedAdministeredAt).format("HH:mm")
        : "",
    },
    validate: {
      staff: (value) => (value ? null : "Required"),
      date: (value) => (value ? null : "Required"),
      time: (value) => (value ? null : "Required"),
    },
  });

  // Helper to refresh admin form from current data
  const populateAdminForm = () => {
    const staffId =
      resolvedStaff?.id ||
      resolvedStaff?._id?.toString() ||
      data?.staff?.id ||
      data?.staff?._id?.toString();
    adminForm.setFieldValue("staff", staffId);
    if (resolvedAdministeredAt) {
      adminForm.setFieldValue("date", new Date(resolvedAdministeredAt));
      adminForm.setFieldValue(
        "time",
        moment(resolvedAdministeredAt).format("HH:mm"),
      );
    } else {
      adminForm.setFieldValue("date", null);
      adminForm.setFieldValue("time", "");
    }
  };

  // Pre‑fill the main form (for the outcome) when the modal is opened in view mode
  useEffect(() => {
    if (isViewOpen) {
      form.setFieldValue("outcome", _medicationType);
      form.setFieldValue("comments", data?.comments);
      form.setFieldValue(
        "createdAt",
        data?.createdAt ? new Date(data.createdAt) : null,
      );

      // Administration Form — use resolvedStaff so all statuses populate correctly
      populateAdminForm();
    } else {
      // For editing an existing entry (i.e., the outcome form is editable)
      if (data?.createdAt) {
        form.setFieldValue("createdAt", new Date(data.createdAt));
      }
      form.setFieldValue(
        "quantity",
        String(data?.form3?.medicationadministered),
      );
      // Also populate admin form in case edit mode is opened later
      populateAdminForm();
    }
  }, [isViewOpen, data, resolvedStaff]);

  // Also populate admin form whenever data changes (redundant but safe)
  useEffect(() => {
    if (data) {
      populateAdminForm();
    }
  }, [data, resolvedStaff]);

  const isValidDate = (d) => {
    if (Object.prototype.toString.call(d) === "[object Date]") {
      if (isNaN(d)) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  const onSave = async (values) => {
    if (
      (Number(values?.quantity) > 0 && values?.outcome === "Taken") ||
      values?.outcome !== "Taken"
    ) {
      const timeSlot = getActiveSlotForDate(data?.timeSlot, data?.date);

      const getCreatedAt = () => {
        if (form.values?.createdAt) {
          return buildCalendarIso(
            form.values.createdAt,
            moment(form.values.createdAt).format("HH:mm"),
          );
        }

        // No date/time explicitly picked — use current date with the slot time
        const now = new Date();
        if (timeSlot) {
          return buildCalendarIso(now, timeSlot);
        }
        return buildCalendarIso(now, moment().format("HH:mm"));
      };

      let obj = {
        client: selectedClient?.id,
        staff: selectedStaff || userid,
        medicationType:
          data?.form3?.medicationType === "PRN" ? "PRN" : values?.outcome,
        date: getCalendarDateKey(data?.date),
        createdAt: getCreatedAt(),
        comments: values?.comments,
        timeSlot: timeSlot,
        medicationDetails: [
          {
            marId: data?._id,
            medicationId: data?.medicationId,
            dosage: values?.outcome === "Taken" ? Number(values?.quantity) : 0,
            createdAt: getCreatedAt(),
            timeSlot: timeSlot,
          },
        ],
      };
      if (!isValidDate(new Date(data?.date))) {
        return showInfo("Please refresh page");
      }

      const resolveStatus = (item) => {
        const mt = item?.medicationType;
        if (Array.isArray(mt) && mt.length > 0) return mt[mt.length - 1].status;
        return mt;
      };

      const sortMedicine = (data) => {
        return data.sort((a, b) => {
          const timeA =
            resolveStatus(a) !== "PRN"
              ? moment(a?.timeSlot, "hh:mm A").format("HH:mm")
              : moment(a?.createdAt).format("HH:mm");
          const timeB =
            resolveStatus(b) !== "PRN"
              ? moment(b?.timeSlot, "hh:mm A").format("HH:mm")
              : moment(b?.createdAt).format("HH:mm");

          return timeA.localeCompare(timeB);
        });
      };

      const dosage = values?.outcome === "Taken" ? Number(values?.quantity) : 0;

      let payload = {
        ...obj,
      };
      const filteredMed = sortMedicine(allData)?.filter((_v) => {
        if (_v?.medicationId !== data?.medicationId) return false;
        return _v?.timeSlot === timeSlot;
      });

      let selectedMedIndex = filteredMed?.findIndex((_fm) => {
        if (String(_fm?._id) !== String(data?._id)) return false;
        return _fm?.timeSlot === timeSlot;
      });

      payload.included = filteredMed
        ?.slice(selectedMedIndex, filteredMed?.length)
        ?.map((_m, index) => {
          if (index == 0) {
            return {
              marId: _m?._id,
              medicationId: _m?.medicationId,
              dosage:
                values?.outcome === "Taken" ? Number(values?.quantity) : 0,
              slot: _m?.timeSlot,
              newMedicationType: values?.outcome,
              createdAt: getCreatedAt(_m),
            };
          } else {
            const existingStatus = (() => {
              const mt = _m?.medicationType;
              if (Array.isArray(mt) && mt.length > 0)
                return mt[mt.length - 1].status;
              return mt;
            })();
            return {
              marId: _m?._id,
              medicationId: _m?.medicationId,
              dosage:
                values?.outcome === "Taken"
                  ? Number(_m?.dosage)
                  : Number(_m?.form3?.medicationadministered),
              slot: _m?.timeSlot,
              newMedicationType: existingStatus,
            };
          }
        });

      setBtnLoading(true);
      try {
        let medicationRemaining =
          data?.form3?.medicineRemaining && data?.form3?.medicineRemaining > 0
            ? data?.form3?.medicineRemaining
            : data?.medicineRemaining;
        if (
          values?.outcome === "Taken" &&
          Number(dosage) > Number(medicationRemaining)
        ) {
          swal(
            "Oops! Something went wrong",
            "Administered Quantity is greater than available medicine count, Kindly select 'Out of Medication' option from outcomes",
            "error",
          );
        } else {
          const res = await createMar(payload);
          if (res) {
            form.reset();
            adminForm.setValues({ staff: undefined, date: null, time: "" });
            setSelectedStaff();
            onclose();
            const calendar_view = localStorage.getItem("calendar_date");
            await getMar(calendar_view, selectedClient?.id);
          }
        }
      } catch (error) {}
      setBtnLoading(false);
    } else {
      alert("Quantity is Required!");
    }
  };

  const handleAdminBy = async (values) => {
    const selectedStaffEntry = populateStaff?.find(
      (s) => s.value === values?.staff,
    );
    const timeSlot = getActiveSlotForDate(data?.timeSlot, data?.date);
    // Use the scheduled calendar date (data.date) as matchDate so the backend
    // matches entry.date — the slot's fixed noon-UTC scheduled date.
    // Previously we used administeredAt, but when multiple slots are marked
    // retrospectively on the same day (without admin time), all entries get the
    // same administeredAt UTC date, causing the backend to always update the
    // first matching entry instead of the correct one.
    const matchDate = getCalendarDateKey(data?.date || date);

    const obj = {
      marId: data?._id,
      staff: values?.staff,
      staffName: selectedStaffEntry?.label || "",
      administeredAt: buildCalendarIso(values?.date, values?.time),
      matchDate,
      timeSlot,
      medicationType: "SCHEDULE",
      form3: data?.form3,
    };
    setBtnLoading(true);
    const res = await updateMedicationAdministration(obj);
    if (res) {
      const calendar_view = localStorage.getItem("calendar_date");
      await getMar(calendar_view, selectedClient?.id);
    }
    setBtnLoading(false);
    setIsEditOpen(false);
    setSelectedStaff();
    onclose();
    setIsEditOpen(false);
  };

  // selectedId: in edit mode use the dropdown value; otherwise use resolvedStaff
  // so that signature lookups work for ALL statuses (Refused, Out of Medication, etc.)
  const selectedId =
    isEditOpen && !(userType === "staff" && role === "staff")
      ? adminForm?.values?.staff
      : resolvedStaff?.id ||
        resolvedStaff?._id?.toString() ||
        data?.staff?.id ||
        data?.staff?._id?.toString();

  const staffSignature = allSignature?.find(({ staff, admin }) => {
    const staffId = staff?._id?.toString() || staff?.id || staff;
    const adminId = admin?._id?.toString() || admin?.id || admin;
    return staffId === selectedId || adminId === selectedId;
  });

  console.log("data", data);

  return (
    <Drawer
      opened={opened}
      onClose={() => {
        setSelectedStaff();
        onclose();
        setIsEditOpen(false);
      }}
      closeOnClickOutside={false}
      withCloseButton={false}
      offset={8}
      size={690}
      sx={{
        ".mantine-Drawer-content": {
          background: "var(--app-surface-2)",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        },
        ".mantine-Drawer-body": {
          padding: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
      zIndex={2000}
      position="right"
      overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
      shadow="md"
    >
      <div className={classes.drawerBody}>
        {/* Header */}
        <div className={classes.modalTopBar}>
          <h3 className={classes.modalTitle}>Medication</h3>
          <button
            type="button"
            className={classes.modalCloseBtn}
            onClick={() => {
              setSelectedStaff();
              onclose();
              setIsEditOpen(false);
            }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable body */}
        <div className={classes.formBox}>
          <div className={classes.prnWrapper}>
            {/* Client card */}
            <div className={classes.viewClientCard}>
              <Flex align="center" gap={12}>
                <Avatar
                  src={decrypt(selectedClient?.imageurl) || null}
                  alt={selectedClient?.name || "client"}
                  radius="xl"
                  size={48}
                />
                <div>
                  <Text className={classes.clientName}>
                    {selectedClient?.name}
                  </Text>
                  <Text className={classes.clientId}>
                    {selectedClient?.id?.slice(0, 6)?.toLowerCase()}
                  </Text>
                </div>
              </Flex>
              {/* Status badge — only for Missed / upcoming */}
              {(_medicationType === "Missed" ||
                _medicationType === "upcoming") && (
                <span
                  className={classes.statusBadge}
                  style={{
                    color: getColor(_medicationType)?.dark,
                    backgroundColor: getColor(_medicationType)?.light,
                    border: `1px solid ${getColor(_medicationType)?.border}`,
                  }}
                >
                  {firstLetterCap(_medicationType)}
                </span>
              )}
              {/* Refresh */}
              {/* <Button
                  variant="subtle"
                  compact
                  style={{ color: "var(--app-text)", fontFamily: "Inter", fontSize: 13, fontWeight: 500 }}
                 
                >
                  ↻ Refresh
                </Button> */}
            </div>

            {/* Medication info card */}
            <div className={classes.contentBox}>
              {isActivity !== true &&
                profile?.user?.role !== "staff" &&
                _medicationType !== _upcoming &&
                _medicationType !== "Missed" && (
                  <Box mb={12}>
                    <DeleteComponent
                      data={data}
                      onclose={() => onclose()}
                      type={"scheduled"}
                    />
                  </Box>
                )}
              <Box className={classes.grayBox}>
                {/* Name + narcotic */}
                <Flex className="medName">
                  <p>{firstLetterCap(data?.form3?.medicationName)}</p>
                  {data?.form3?.isNarcotic === "Yes" && (
                    <span className={classes.narcoticBadge}>Narcotic</span>
                  )}
                </Flex>
                <Box
                  className={classes.medInfoBox}
                  style={
                    isViewOpen || _medicationType === "Missed"
                      ? { backgroundColor: getColor(_medicationType)?.light }
                      : undefined
                  }
                >
                  {/* Frequency | Remaining */}
                  <Flex className={classes.medFreqAndCountRow}>
                    <Box className="freqAndTime">
                      <p className="frequency">{data?.form3?.frequency}</p>
                      <p className="time">{data?.timeSlot}</p>
                    </Box>
                    <Box className="freqAndTime">
                      <p className="frequency">
                        {resolvedMedicineRemaining === "unlimited" ||
                        data?.isUnlimited ? (
                          <Box className={classes.unlimitedBadge}>
                            <Text className={classes.unlimitedText}></Text>
                            Unlimited
                          </Box>
                        ) : (
                          (resolvedMedicineRemaining ?? "—")
                        )}
                      </p>
                      {!data?.isUnlimited &&
                        resolvedMedicineRemaining !== "unlimited" && (
                          <p className="time">
                            of {data?.totalMedicine} Remaining
                          </p>
                        )}
                    </Box>
                  </Flex>
                  <Flex className={classes.quantityAndInfoRow}>
                    <Box>
                      <p className="name">
                        {data?.form3?.medicationName}
                        {data?.form3?.dosage
                          ? ` (${data.form3.dosage}${data.form3.unit || ""})`
                          : ""}
                      </p>
                      <p className="dosage">
                        {[data?.form3?.ingestionMode, data?.form3?.dosageForm]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                    </Box>
                    {isViewOpen ? (
                      _medicationType === "Taken" && (
                        <Box className={classes.viewQtyBox}>
                          <p className={classes.viewQtyNumber}>
                            {_dosageFromEntry}
                          </p>
                          <p className={classes.viewQtyLabel}>
                            Quantity Dispensed
                          </p>
                        </Box>
                      )
                    ) : (
                      <Box>
                        <p className={classes.labelStyle}>Quantity Dispensed</p>
                        <Flex className={classes.quantityRow}>
                          <ActionIcon
                            className={classes.qtyBtn}
                            disabled={futureDate == 1}
                            onClick={() =>
                              form.setFieldValue(
                                "quantity",
                                String(
                                  Math.max(0, Number(form.values.quantity) - 1),
                                ),
                              )
                            }
                          >
                            <TiMinus />
                          </ActionIcon>
                          <input
                            id="change-quantity"
                            type="number"
                            className={classes.qtyInput}
                            value={form.values.quantity}
                            disabled={futureDate == 1}
                            onChange={(e) => {
                              let value = e.target.value;
                              if (value.includes(".")) {
                                const [integerPart, decimalPart] =
                                  value.split(".");
                                if (decimalPart.length > 1) {
                                  value = `${integerPart}.${decimalPart.slice(0, 1)}`;
                                }
                              }
                              form.setFieldValue("quantity", value);
                            }}
                            onWheel={(e) => e.target.blur()}
                          />
                          <ActionIcon
                            className={classes.qtyBtn}
                            disabled={futureDate == 1}
                            onClick={() =>
                              form.setFieldValue(
                                "quantity",
                                String(Number(form.values.quantity) + 1),
                              )
                            }
                          >
                            <FaPlus />
                          </ActionIcon>
                        </Flex>
                      </Box>
                    )}
                  </Flex>
                  {isViewOpen ? (
                    <>
                      <div className={classes.viewInfoBox}>
                        <Flex className={classes.viewInfoRow}>
                          <Box>
                            <p className={classes.viewInfoLabel}>
                              Use of medication
                            </p>
                            <p className={classes.viewInfoValue}>
                              {data?.form3?.medicationUse || "N/A"}
                            </p>
                          </Box>
                          <Box>
                            <p className={classes.viewInfoLabel}>
                              Medication Instruction
                            </p>
                            <p className={classes.viewInfoValue}>
                              {data?.form3?.medicationInstruction || "N/A"}
                            </p>
                          </Box>
                        </Flex>
                      </div>
                      {data?.form3?.medicationType !== "PRN" &&
                        form.values.outcome && (
                          <div className={classes.outcomesSection}>
                            <p className={classes.outcomesHeading}>Outcomes</p>
                            <Flex className={classes.outcomeRow}>
                              <img
                                src={
                                  {
                                    Taken: takenMed,
                                    Refused: refusedMed,
                                    "Unable to Take": unableToTakeMed,
                                    "Out of Medication": outOfMed,
                                  }[form.values.outcome]
                                }
                                alt={form.values.outcome}
                              />
                              <span
                                className={classes.outcomeLabelDynamic}
                                style={{
                                  color: getColor(form.values.outcome)?.dark,
                                }}
                              >
                                {form.values.outcome}
                              </span>
                            </Flex>
                          </div>
                        )}
                    </>
                  ) : (
                    <Flex align="flex-start" gap={18}>
                      {/* Use of medication accordion */}
                      <Accordion
                        variant="separated"
                        className={classes.medAcc}
                        classNames={{
                          item: classes.accordionSelectItem,
                          control: classes.accordionSelectControl,
                        }}
                      >
                        <Accordion.Item value="use">
                          <Accordion.Control>
                            Use of medication
                          </Accordion.Control>
                          <Accordion.Panel>
                            {data?.form3?.medicationUse || "—"}
                          </Accordion.Panel>
                        </Accordion.Item>
                      </Accordion>
                      {/* Medication instruction accordion */}
                      <Accordion
                        variant="separated"
                        className={classes.medAcc}
                        classNames={{
                          item: classes.accordionSelectItem,
                          control: classes.accordionSelectControl,
                        }}
                      >
                        <Accordion.Item value="instruction">
                          <Accordion.Control>
                            Medication instruction
                          </Accordion.Control>
                          <Accordion.Panel>
                            {data?.form3?.medicationInstruction || "—"}
                          </Accordion.Panel>
                        </Accordion.Item>
                      </Accordion>
                    </Flex>
                  )}
                </Box>
              </Box>

              {/* Main form: staff select + outcome + comment + date/time + save */}
              <form
                className={classes.modalBody}
                onSubmit={form?.onSubmit((values) => onSave(values))}
              >
                {/* Staff select (for Missed / upcoming — non-staff role only) */}
                {(_medicationType === "Missed" ||
                  _medicationType === "upcoming") && (
                  <>
                    {profile?.user?.role !== "staff" ? (
                      <Select
                        className={classes.inputStyle}
                        data={populateStaff}
                        value={selectedStaff}
                        placeholder="Select Staff"
                        label="Select Staff"
                        onChange={(e) => setSelectedStaff(e)}
                        rightSection={<IconChevronDown size={16} />}
                        withinPortal={false}
                        dropdownPosition="bottom"
                        maxDropdownHeight={200}
                      />
                    ) : (
                      <div className={classes.medicineInnerCard}>
                        <Text fz={14} fw={600} ff="Inter" color="var(--app-text)">
                          {profile?.user?.name}
                        </Text>
                      </div>
                    )}
                  </>
                )}

                {/* Outcome toggles (non-PRN, edit mode only — view mode shows outcomes inside medicine card) */}
                {data?.form3?.medicationType !== "PRN" && !isViewOpen && (
                  <Radio.Group
                    label=""
                    defaultValue={form?.values.outcome}
                    onChange={(value) => {
                      form.setFieldValue("outcome", value);
                      if (value === "Taken") {
                        setValidation({
                          outcome: (value) => (value ? null : "Required"),
                        });
                      } else {
                        setValidation({
                          outcome: (value) => (value ? null : "Required"),
                          comments: (value) => (value ? null : "Required"),
                        });
                      }
                    }}
                    required
                    withAsterisk
                  >
                    <Flex className={classes.statusRadioRow}>
                      <Radio
                        id="taken-medicine"
                        disabled={isViewOpen || futureDate == 1}
                        value="Taken"
                        color="green"
                        label={
                          <Flex
                            className={classes.statusLabel}
                            style={{ color: getColor("Taken")?.dark }}
                          >
                            <img src={takenMed} alt="taken" />
                            Taken
                          </Flex>
                        }
                      />
                      <Radio
                        disabled={isViewOpen || futureDate == 1}
                        value="Refused"
                        color="blue"
                        label={
                          <Flex
                            className={classes.statusLabel}
                            style={{ color: getColor("Refused")?.dark }}
                          >
                            <img src={refusedMed} alt="refused" />
                            Refused
                          </Flex>
                        }
                      />
                      <Radio
                        disabled={isViewOpen || futureDate == 1}
                        value="Unable to Take"
                        color="yellow"
                        label={
                          <Flex
                            className={classes.statusLabel}
                            style={{
                              color: getColor("Unable to Take")?.dark,
                            }}
                          >
                            <img src={unableToTakeMed} alt="unable" />
                            Unable to take
                          </Flex>
                        }
                      />
                      <Radio
                        disabled={isViewOpen || futureDate == 1}
                        value="Out of Medication"
                        color="gray"
                        label={
                          <Flex
                            className={classes.statusLabel}
                            style={{
                              color: getColor("Out of Medication")?.dark,
                            }}
                          >
                            <img src={outOfMed} alt="out" />
                            Out of medication
                          </Flex>
                        }
                      />
                    </Flex>
                  </Radio.Group>
                )}

                {/* Comment */}
                <Textarea
                  className={classes.inputStyle}
                  id="dosage-completed"
                  minRows={4}
                  label="Comment"
                  readOnly={isViewOpen || futureDate == 1}
                  placeholder="Enter comment here"
                  defaultValue={form.values.comments}
                  {...form.getInputProps("comments")}
                />

                {/* Date/time picker */}
                {!isViewOpen && (
                  <Flex gap="md" mt={8} mb={8}>
                    <DatePickerInput
                      label="Administration Date"
                      placeholder="Pick a date"
                      value={
                        form.values.createdAt
                          ? new Date(form.values.createdAt)
                          : null
                      }
                      onChange={(newDate) => {
                        if (newDate) {
                          const dt = new Date(newDate);
                          const now = new Date();
                          if (form.values.createdAt) {
                            const existing = new Date(form.values.createdAt);
                            dt.setHours(
                              existing.getHours(),
                              existing.getMinutes(),
                            );
                          } else {
                            dt.setHours(now.getHours(), now.getMinutes());
                          }
                          form.setFieldValue("createdAt", dt);
                        } else {
                          form.setFieldValue("createdAt", null);
                        }
                      }}
                      maxDate={new Date()}
                      icon={<FiCalendar />}
                      clearable
                      disabled={futureDate == 1}
                      className={classes.inputStyle}
                      style={{ flex: 1 }}
                    />
                    <TimeInput
                      label="Administration Time"
                      placeholder="Pick a time"
                      value={
                        form.values.createdAt
                          ? dayjs(form.values.createdAt).format("HH:mm")
                          : ""
                      }
                      onChange={(e) => {
                        const newTime = e.currentTarget.value;
                        if (newTime) {
                          const [hours, minutes] = newTime.split(":");
                          const dt = form.values.createdAt
                            ? new Date(form.values.createdAt)
                            : new Date();
                          dt.setHours(parseInt(hours), parseInt(minutes));
                          form.setFieldValue("createdAt", dt);
                        }
                      }}
                      icon={<IconClock />}
                      format="12"
                      withDropdown
                      disabled={futureDate == 1}
                      className={classes.inputStyle}
                      style={{ flex: 1 }}
                    />
                  </Flex>
                )}

                {/* Save button */}
                {!isViewOpen && (
                  <Button
                    id="click-to-submit-dosage"
                    loading={btnLoading}
                    type="submit"
                    fullWidth
                    className={classes.prnSaveBtn}
                    disabled={futureDate == 1 || !form.values.outcome}
                  >
                    Save
                  </Button>
                )}
              </form>

              {/* ── View mode: Medication details card ── */}
              {isViewOpen && !isEditOpen && (
                <div className={classes.medDetailsCard}>
                  <Flex justify="space-between" align="center" mb={12}>
                    <p className={classes.medDetailsTitle}>
                      Medication details
                    </p>
                    {!(userType === "staff" && role === "staff") &&
                      isActivity !== true && (
                        <Button
                          className={classes.editBtnOrange}
                          onClick={() => {
                            populateAdminForm();
                            setIsEditOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                  </Flex>
                  <div className={classes.medDetailsInner}>
                    <div className={classes.medDetailsCol}>
                      <p className={classes.medDetailsLabel}>Administered by</p>
                      <p className={classes.medDetailsValue}>
                        {resolvedStaff?.name || data?.staff?.name || "—"}
                      </p>
                    </div>
                    <div className={classes.medDetailsCol}>
                      <p className={classes.medDetailsLabel}>Signature</p>
                      {staffSignature?.signature ||
                      staffSignature?.signatureImage ? (
                        <img
                          className={classes.medDetailsSigImg}
                          src={
                            decrypt(staffSignature?.signature) ||
                            decrypt(staffSignature?.signatureImage)
                          }
                          alt="signature"
                        />
                      ) : staffSignature?.signatureText ? (
                        <p className={classes.medDetailsSigText}>
                          {staffSignature?.signatureText}
                        </p>
                      ) : (
                        <Text color="dimmed" size="sm" fontFamily={"Inter"}>
                          No signature found
                        </Text>
                      )}
                    </div>
                    <div className={classes.medDetailsCol}>
                      <p className={classes.medDetailsLabel}>Date</p>
                      <p className={classes.medDetailsValue}>
                        {resolvedAdministeredAt
                          ? moment(resolvedAdministeredAt).format("MM/DD/YYYY")
                          : "—"}
                      </p>
                    </div>
                    <div className={classes.medDetailsCol}>
                      <p className={classes.medDetailsLabel}>Time</p>
                      <p className={classes.medDetailsValue}>
                        {_medicationType !== _upcoming && resolvedAdministeredAt
                          ? moment(resolvedAdministeredAt).format("hh:mm a")
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Edit mode: Admin details form ── */}
              {isViewOpen && isEditOpen && (
                <form
                  onSubmit={adminForm?.onSubmit((values) =>
                    handleAdminBy(values),
                  )}
                >
                  <div className={classes.medDetailsCard}>
                    <Flex justify="space-between" align="center" mb={12}>
                      <p className={classes.medDetailsTitle}>
                        Medication details
                      </p>
                      <Flex gap={8}>
                        <Button
                          className={classes.cancelBtn}
                          onClick={() => setIsEditOpen(false)}
                        >
                          Cancel
                        </Button>
                        {!(userType === "staff" && role === "staff") && (
                          <Button
                            loading={btnLoading}
                            type="submit"
                            className={classes.editBtnOrange}
                          >
                            Save
                          </Button>
                        )}
                      </Flex>
                    </Flex>
                    <div className={classes.medDetailsInner}>
                      <div className={classes.editDetailsGrid}>
                        {/* Select Staff */}
                        <div className={classes.medDetailsCol}>
                          {!(userType === "staff" && role === "staff") ? (
                            <Select
                              className={classes.inputStyle}
                              data={populateStaff?.map((d) => ({
                                value: d.value,
                                label: d.label,
                              }))}
                              placeholder="Select Staff"
                              {...adminForm.getInputProps("staff")}
                              rightSection={<IconChevronDown size={16} />}
                              withinPortal={false}
                              label="Select Staff"
                            />
                          ) : (
                            <p className={classes.medDetailsValue}>
                              {resolvedStaff?.name || data?.staff?.name}
                            </p>
                          )}
                        </div>
                        {/* Signature */}
                        <div className={classes.medDetailsCol}>
                          <p className={classes.labelStyle}>Signature</p>
                          <div className={classes.sigBox}>
                            {staffSignature?.signature ||
                            staffSignature?.signatureImage ? (
                              <img
                                className={classes.medDetailsSigImg}
                                src={
                                  decrypt(staffSignature?.signature) ||
                                  decrypt(staffSignature?.signatureImage)
                                }
                                alt="signature"
                              />
                            ) : staffSignature?.signatureText ? (
                              <p className={classes.medDetailsSigText}>
                                {staffSignature?.signatureText}
                              </p>
                            ) : (
                              <p className={classes.medDetailsValue}>—</p>
                            )}
                          </div>
                        </div>
                        {/* Select Date */}
                        <div className={classes.medDetailsCol}>
                          {!(userType === "staff" && role === "staff") ? (
                            <DateInput
                              className={classes.inputStyle}
                              placeholder="Pick a date"
                              {...adminForm.getInputProps("date")}
                              label="Select date"
                              icon={<FiCalendar />}
                            />
                          ) : (
                            <p className={classes.medDetailsValue}>
                              {moment(data?.createdAt).format("MM/DD/YYYY")}
                            </p>
                          )}
                        </div>
                        {/* Select Time */}
                        <div className={classes.medDetailsCol}>
                          {!(userType === "staff" && role === "staff") ? (
                            <TimeInput
                              className={classes.inputStyle}
                              ref={ref}
                              label="Select time"
                              icon={
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  onClick={() => ref?.current?.showPicker()}
                                >
                                  <IconClock
                                    style={{ width: rem(16), height: rem(16) }}
                                    stroke={1.5}
                                  />
                                </ActionIcon>
                              }
                              classNames={{ icon: classes.timeInputLeftIcon }}
                              {...adminForm.getInputProps("time")}
                            />
                          ) : (
                            <p className={classes.medDetailsValue}>
                              {_medicationType !== _upcoming &&
                              resolvedAdministeredAt
                                ? moment
                                    .tz(
                                      moment(resolvedAdministeredAt),
                                      profile?.user?.timeZone,
                                    )
                                    .format("hh:mm a")
                                : "—"}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default MedicationModal;
